import { useState, useRef } from 'react';
import { BookOpen, Check, Sparkles } from 'lucide-react';
import { Button } from '../../../components/ui';
import Input from '../../../components/ui/Input';
import ShakeWrapper from './ShakeWrapper';
import ChapterContextReader from './ChapterContextReader';
import ContextPassage from './ContextPassage';
import ExampleSentenceHint from './ExampleSentenceHint';

// "Context recognition" question type — student reads a passage and
// types an open-ended guess at what the target word means. Today only
// wired into onboarding Stage 1; lives in shared/ so a future review-
// side variant renders the same question without duplication. See the
// §3.23 "Question-Type Contract" entry in the vocabulary spec.

// Stage 1 state machine:
//   input            — initial, or post-shake retry for attempt 2
//   submitted_yes    — answered correctly (shows read-only box + optional acknowledgment)
//   submitted_sort_of — partially right (read-only box + blue AI explanation)
//   way_off_first    — missed once; shake + red nudge, then clear for attempt 2
//   way_off_final    — missed twice; read-only box + red "Not quite." + blue explanation
//
// After an attempt resolves, the submitted answer is preserved on screen in
// a read-only, input-shaped box so the student can see what they wrote next
// to the AI feedback. The parent (OnboardingShow) reads `data.next` from
// the submit response to decide whether to show its Continue button.
export default function ContextRecognition({
  lemma,
  contextSentence,
  contextBookTitle,
  contextChapterTitle,
  additionalExamples,
  prompt,
  chapterContext,
  onSubmit,
  // §3.24 — async fetcher invoked when the student opens the example-hint
  // panel and no cached example was passed in `additionalExamples`.
  // Returns the generated sentence string, or null if generation failed.
  onRequestExample,
  // Controlled props — when provided, the parent owns text + attempts
  // state. This is what OnboardingActivity uses so it can broadcast the
  // student's transient feedback (the AI's tier + explanation) to the
  // teacher's observer mirror via progress_state. The solo onboarding
  // flow passes these through too; only the parent has changed.
  text: controlledText,
  attempts: controlledAttempts,
  onTextChange,
  onAttempt,
  // Controlled example-hint state (live-session bidirectional sync).
  // When provided, hint open/close + lazily-fetched example flow up to
  // the parent (OnboardingActivity) so progress_state can carry them
  // to the peer. Falls back to the component's own state for solo
  // usage where no live-session parent is wired.
  hintOpen: controlledHintOpen,
  hintFetchedExample: controlledHintFetched,
  onHintOpenChange,
  onHintFetchedExampleChange,
}) {
  // Local fallback state for any caller that doesn't provide controlled
  // props yet — preserves the original behavior pre-controlled-refactor.
  const [localText, setLocalText] = useState('');
  const [localAttempts, setLocalAttempts] = useState([]);

  const text = controlledText !== undefined ? controlledText : localText;
  const attempts = controlledAttempts !== undefined ? controlledAttempts : localAttempts;

  const setText = (v) => {
    if (typeof onTextChange === 'function') onTextChange(v);
    else setLocalText(v);
  };
  const pushAttempt = (newAttempt, replace = false) => {
    if (typeof onAttempt === 'function') {
      onAttempt(newAttempt, { replace, attemptsLength: attempts.length });
    } else {
      setLocalAttempts((a) => (replace ? [newAttempt] : [...a, newAttempt]));
    }
  };

  const [loading, setLoading] = useState(false);
  const shakeRef = useRef(null);

  const lastAttempt = attempts[attempts.length - 1] ?? null;
  const terminal =
    lastAttempt && (lastAttempt.tier !== 'way_off' || attempts.length >= 2);

  // §3.24 — tracks whether the student has opened the example-hint panel
  // at any point on this card. Read at submit time and forwarded to the
  // back-end for analytics (no FSRS penalty). Cleared by the parent's
  // card-key change when remounting on the next card.
  const hintUsedRef = useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() || loading) return;

    setLoading(true);
    try {
      const result = await onSubmit(text.trim(), { hint_used: hintUsedRef.current });
      const newAttempt = {
        text: text.trim(),
        tier: result?.tier || 'sort_of',
        explanation: result?.explanation || '',
      };

      if (newAttempt.tier === 'way_off' && attempts.length === 0) {
        // First way_off: shake input, then clear for attempt 2.
        shakeRef.current?.shake();
        pushAttempt(newAttempt, true);
        setTimeout(() => setText(''), 400);
      } else {
        // Terminal: yes / sort_of / way_off_final.
        pushAttempt(newAttempt);
      }
    } finally {
      setLoading(false);
    }
  };

  const questionPane = (
    <div className="space-y-6">
      {/* Prompt */}
      <p className="text-lg text-gray-700 dark:text-gray-300">
        {prompt || (
          <>
            In this context, <span className="font-serif italic">{lemma}</span>{' '}
            means ___________
          </>
        )}
      </p>

      {terminal ? (
        <TerminalView attempt={lastAttempt} />
      ) : (
        <ShakeWrapper ref={shakeRef}>
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              // Remount on retry so autoFocus re-fires after the shake clears the text.
              key={`attempt-${attempts.length}`}
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your answer..."
              spellCheck={false}
              data-gramm="false"
              // Stable key for the live-session typing mirror.
              data-live-input-key="stage1-context"
              autoComplete="off"
              disabled={loading}
              className={
                lastAttempt?.tier === 'way_off'
                  ? 'border-amber-400 dark:border-amber-500'
                  : ''
              }
            />
            {/* Red redirect stays visible through attempt 2 */}
            {lastAttempt?.tier === 'way_off' && lastAttempt.explanation && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {lastAttempt.explanation}
              </p>
            )}
            <Button type="submit" disabled={!text.trim() || loading}>
              {loading ? 'Checking...' : 'Submit'}
            </Button>
          </form>
        </ShakeWrapper>
      )}

      {/* §3.24 — collapsible example-hint, available from attempt 1.
          Opens to one cached WordExampleSentence; lazily generates a
          fresh one if the cache only has the sentence used as primary
          context. Tracked via hintUsedRef and reported to the back-end
          at submit. No FSRS penalty.
          Stays mounted through the resolved (terminal) state so the
          student / teacher can keep the example visible alongside the
          answer feedback until they press Continue / Step forward.
          When OnboardingActivity wires the controlled props, open
          state + the lazily-fetched example sync bidirectionally
          between student and teacher via progress_state / peer_intent. */}
      <ExampleSentenceHint
        lemma={lemma}
        initialExamples={additionalExamples}
        onRequestMore={onRequestExample}
        onHintUsed={() => {
          hintUsedRef.current = true;
        }}
        open={controlledHintOpen}
        fetched={controlledHintFetched}
        onOpenChange={onHintOpenChange}
        onFetchedChange={onHintFetchedExampleChange}
      />
    </div>
  );

  // Split-pane layout when chapter context is available
  if (chapterContext) {
    return (
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 -mx-4 lg:-mx-8">
        {/* Left pane: chapter reader */}
        <div className="lg:w-3/5 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700">
          <ChapterContextReader
            fullHtml={chapterContext.full_html}
            targetWord={lemma}
            targetSentence={chapterContext.target_sentence}
            bookTitle={chapterContext.book_title}
            chapterTitle={chapterContext.chapter_title}
          />
        </div>
        {/* Right pane: question */}
        <div className="lg:w-2/5 lg:sticky lg:top-0 lg:self-start px-4 lg:px-0 lg:pr-4">
          {questionPane}
        </div>
      </div>
    );
  }

  // Fallback: isolated-sentence display (no chapter source)
  return (
    <div className="space-y-8">
      {/* Context sentence */}
      <div className="text-center">
        <ContextPassage
          text={contextSentence}
          highlight={lemma}
          className="font-serif text-xl md:text-2xl leading-relaxed text-gray-800 dark:text-gray-200"
        />
        {(contextBookTitle || contextChapterTitle) && (
          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <BookOpen className="w-3.5 h-3.5 flex-shrink-0" />
            <span>
              {[contextBookTitle, contextChapterTitle].filter(Boolean).join(' — ')}
            </span>
          </p>
        )}
      </div>

      {/* Additional examples used to render always-visible here. Now
          surfaced via the collapsible ExampleSentenceHint inside
          questionPane below, so the student opts in rather than the UI
          dumping every cached example up front. (§3.24) */}

      {questionPane}
    </div>
  );
}

function ReadOnlyAnswer({ text }) {
  return (
    <div className="w-full px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white">
      {text}
    </div>
  );
}

function AiSuggestion({ text }) {
  return (
    <div className="flex items-start gap-2 rounded-lg p-3 bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
      <Sparkles className="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-blue-900 dark:text-blue-200">{text}</p>
    </div>
  );
}

// Guard against the LLM echoing a placeholder like "..." in place of a real
// sentence. Treats anything that's only whitespace or punctuation as empty.
function meaningfulText(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return null;
  if (/^[\s.…!?,\-–—]+$/.test(trimmed)) return null;
  return trimmed;
}

function TerminalView({ attempt }) {
  const { text, tier, explanation } = attempt;
  const meaningful = meaningfulText(explanation);

  return (
    <div className="space-y-3">
      <ReadOnlyAnswer text={text} />

      {tier === 'yes' && (
        <div className="flex items-start gap-1.5 text-green-600 dark:text-green-400">
          <Check className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
          <span className="text-sm font-medium">{meaningful ?? 'Got it.'}</span>
        </div>
      )}

      {tier === 'sort_of' && meaningful && <AiSuggestion text={meaningful} />}

      {tier === 'way_off' && (
        <>
          <p className="text-sm text-red-600 dark:text-red-400">Not quite.</p>
          {meaningful && <AiSuggestion text={meaningful} />}
        </>
      )}
    </div>
  );
}
